import { JwApiError, JwAuthError, auth } from './chunk-MHUCDFWG.js';
import { getEnvVariable } from './chunk-YYYI4WOV.js';
import { __name } from './chunk-7QVYU63E.js';

// ../base/fetch/util.ts
function parseQueryParams(usp, params) {
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      if (key.endsWith("[]")) {
        for (const v of value) {
          if (typeof value !== "undefined") {
            usp.append(key, String(v));
          }
        }
      } else {
        usp.set(key, value.join(","));
      }
    } else {
      if (typeof value !== "undefined") {
        usp.set(key, String(value));
      }
    }
  }
}
__name(parseQueryParams, "parseQueryParams");
function parseHeaders(headers, {
  authToken,
  contentType,
  transformHeaders
}) {
  let _headers = { ...headers };
  const setContentTypeHeader = _headers["Content-Type"] !== null;
  if (setContentTypeHeader) {
    switch (contentType) {
      case "json":
        _headers["Content-Type"] = "application/json";
        break;
      case "urlencoded":
        _headers["Content-Type"] = "application/x-www-form-urlencoded";
        break;
      case "none":
        delete _headers["Content-Type"];
    }
  }
  if (authToken && !_headers.Authorization) {
    _headers.Authorization = `${authToken}`;
  }
  if (transformHeaders) {
    _headers = transformHeaders(_headers);
  }
  return Object.entries(_headers).reduce((acc, [key, value]) => {
    if (typeof value !== "undefined" && value !== null) {
      acc[key] = String(value);
    }
    return acc;
  }, {});
}
__name(parseHeaders, "parseHeaders");
function isEmptyResponse(res) {
  if ([202, 204].includes(res.status)) {
    return true;
  }
  const contentLength = "headers" in res ? res.headers.get("content-length") : "getResponseHeader" in res ? res.getResponseHeader("content-length") : void 0;
  if (typeof contentLength === "string" && contentLength === "0" || typeof contentLength === "number" && contentLength === 0) {
    return true;
  }
  if (typeof window !== "undefined" && res instanceof XMLHttpRequest && !res.responseText) {
    return true;
  }
  return false;
}
__name(isEmptyResponse, "isEmptyResponse");

// ../base/fetch/index.ts
function responseToPlainObj(response) {
  const { headers, ok, redirected, status, statusText, type, url } = response;
  return {
    // Headers to plain object
    headers: Array.from(headers.entries()).reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {}),
    ok,
    redirected,
    status,
    statusText,
    type,
    url
  };
}
__name(responseToPlainObj, "responseToPlainObj");
async function parseResponse(response, responseMethod, debug = false) {
  const plainResponse = responseToPlainObj(response);
  const isValidHttpStatus = response.status >= 200 && response.status < 300;
  const isResponseOk = response.ok && isValidHttpStatus;
  let data;
  try {
    data = isEmptyResponse(response) ? void 0 : await response[responseMethod]();
    if (isResponseOk) {
      const ret2 = {
        ...plainResponse,
        data
      };
      debug && console.debug("Response:", JSON.stringify(ret2, null, 2));
      return ret2;
    }
  } catch (e) {
  }
  const ret = {
    ...plainResponse,
    // Maybe the server returned some error JSON that we managed to parse...
    // Blobs are not logged in any case.
    data: data instanceof Blob ? void 0 : data
  };
  debug && console.debug("Error response:", JSON.stringify(ret, null, 2));
  throw new JwApiError(ret);
}
__name(parseResponse, "parseResponse");
function objToUrlSearchParams(element, key = "", usp = new URLSearchParams()) {
  if (typeof element === "object") {
    for (const idx in element) {
      objToUrlSearchParams(element[idx], key ? `${key}[${idx}]` : idx, usp);
    }
  } else {
    usp.set(key, element);
  }
  return usp;
}
__name(objToUrlSearchParams, "objToUrlSearchParams");
async function wrappedFetch(url, fetchOpts, customOpts) {
  const inputType = customOpts.inputType ?? (typeof fetchOpts.body === "object" ? "json" : void 0);
  let body;
  if (typeof fetchOpts.body !== "undefined") {
    switch (inputType) {
      case "json":
        body = JSON.stringify(fetchOpts.body);
        break;
      case "urlencoded":
        body = objToUrlSearchParams(fetchOpts.body);
        break;
      case "none":
        body = void 0;
        break;
      default:
        body = String(fetchOpts.body);
        break;
    }
  }
  const headers = parseHeaders(fetchOpts.headers, {
    contentType: inputType,
    authToken: customOpts.authToken,
    transformHeaders: customOpts.transformHeaders
  });
  const req = {
    ...fetchOpts,
    headers,
    body,
    credentials: getEnvVariable("TEST") ? "include" : "same-origin"
  };
  customOpts.debug && console.debug(
    "Request:",
    (() => {
      const { signal, ...rest } = req;
      return { url, ...rest };
    })()
  );
  const response = customOpts.mockFetch ? await customOpts.mockFetch(url, req) : await fetch(url, req);
  const fetchResponse = await parseResponse(response, customOpts.responseType ?? "json", customOpts.debug);
  return fetchResponse;
}
__name(wrappedFetch, "wrappedFetch");

// ../base/util/url.ts
function joinParts(...parts) {
  return parts.filter(Boolean).join("/").split("/").filter(Boolean).join("/");
}
__name(joinParts, "joinParts");
function joinUrl(...parts) {
  if (parts[0]?.startsWith("http")) {
    const url = new URL(parts.join("/"));
    url.pathname = `/${joinParts(...url.pathname.split("/"))}`;
    return url.toString();
  }
  return joinParts(...parts);
}
__name(joinUrl, "joinUrl");

// ../base/api/list-operation/index.ts
function constructListQuery(q, i = 0) {
  const strings = q.children.map((child) => {
    if (typeof child === "object") {
      return constructListQuery(child, i + 1);
    }
    return child?.trim();
  }).filter(Boolean);
  if (!strings.length) {
    return "";
  }
  const inner = strings.join(` ${q.operator} `).trim();
  return i === 0 ? inner : `( ${inner} )`;
}
__name(constructListQuery, "constructListQuery");
function constructSortString(sort) {
  return (sort ?? []).map((s) => {
    if (s && typeof s === "object" && "field" in s && "order" in s && s.field && s.order) {
      return `${s.field}:${s.order}`;
    }
    if (s && typeof s === "string") {
      return s;
    }
  }).filter(Boolean).join(",");
}
__name(constructSortString, "constructSortString");
function parseListParams(usp, params) {
  if (params?.page) {
    usp.set("page", String(params.page));
  }
  if (params?.page_length) {
    usp.set("page_length", String(params.page_length));
  }
  usp.delete("sort");
  usp.delete("q");
  if (params?.sort) {
    const str = constructSortString(params.sort);
    if (str) {
      usp.set("sort", str);
    }
  }
  if (params?.q) {
    const str = typeof params.q === "string" ? params.q.trim() : constructListQuery(params.q);
    if (str) {
      usp.set("q", str);
    }
  }
}
__name(parseListParams, "parseListParams");

// ../base/api/index.ts
var parseResourcePath = /* @__PURE__ */ __name((endpoint, routeParams, queryParams) => {
  let ret = endpoint;
  if (routeParams) {
    for (const [key, value] of Object.entries(routeParams)) {
      ret = ret.replace(`:${key}`, value ? String(value) : "");
    }
  }
  const matches = ret.match(/:([\w\d]+)/gim);
  if (matches !== null) {
    throw new Error(`Missing route param(s): ${matches.map((m) => m.substring(1))}`);
  }
  if (queryParams) {
    const usp = new URLSearchParams();
    parseQueryParams(usp, queryParams);
    parseListParams(usp, queryParams);
    const queryString = usp.toString();
    if (queryString) {
      ret += `?${queryString}`;
    }
  }
  return ret;
}, "parseResourcePath");
var parseInput = /* @__PURE__ */ __name(async (input, zodObject, transformFn) => {
  let ret;
  if (zodObject) {
    const parsed = zodObject.safeParse(input);
    if ("error" in parsed && parsed.error) {
      throw new Error(parsed.error.toString());
    }
    ret = parsed.data;
  }
  if (transformFn) {
    ret = await transformFn(ret);
  }
  return ret;
}, "parseInput");
async function request({ baseUrl, operations }, action, params, { debug, authToken, transformHeaders, ...opts } = {}) {
  const operation = operations[action];
  let { routeParams } = params;
  if (operation.authenticated && !authToken) {
    throw new JwAuthError("MISSING_TOKEN");
  }
  if (operation.transform?.routeParams && routeParams) {
    routeParams = operation.transform.routeParams(routeParams);
  }
  const inputTransformed = await parseInput(params.input, operation.input, operation.transform?.input);
  const headersTransformed = await parseInput(params.headers, operation.headers, operation.transform?.headers);
  const queryParamsTransformed = await parseInput(params.queryParams, operation.queryParams);
  const parsedPath = parseResourcePath(operation.path, routeParams, queryParamsTransformed);
  const fullUrl = joinUrl(baseUrl(), parsedPath);
  try {
    const res = await wrappedFetch(
      fullUrl,
      {
        ...opts,
        method: operation.method,
        body: inputTransformed,
        headers: {
          ...headersTransformed,
          // Ensure we keep any headers passed in the generic fetch options
          ...opts.headers
        }
      },
      {
        authToken: operation.authenticated ? authToken : void 0,
        inputType: operation.inputType,
        responseType: operation.responseType,
        mockFetch: operation.mockFetch,
        debug,
        transformHeaders
      }
    );
    const { data, ...finalRes } = res;
    let finalData = data;
    if (operation.transform?.output) {
      finalData = await operation.transform.output(data, {
        routeParams,
        queryParams: queryParamsTransformed,
        input: params.input
      });
      debug && console.debug("Transformed:", JSON.stringify(finalData, null, 2));
    }
    operation.events?.onSuccess?.(finalRes);
    if (getEnvVariable("TEST")) {
      finalRes.__operation__ = operation;
    }
    return {
      ...finalRes,
      data: finalData
    };
  } catch (err) {
    operation.events?.onError?.(
      // TS can't determine the correct output type here
      err
    );
    throw err;
  }
}
__name(request, "request");

// src/api/index.ts
async function jwApi(service, action, params, opts) {
  const operations = (await (await import('./conf-5YZ7PRR4.js')).default[service]()).default;
  const authToken = opts?.authToken ?? auth.getToken();
  return request(operations, action, params, {
    ...opts,
    authToken
  });
}
__name(jwApi, "jwApi");

export { isEmptyResponse, jwApi };
//# sourceMappingURL=chunk-O5W6OKX7.js.map
//# sourceMappingURL=chunk-O5W6OKX7.js.map